import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { allGuideArticles } from '@/mocks/seoContent';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50 border-b border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="font-heading text-xl md:text-2xl text-foreground-900 tracking-tight">
              VIGORBUY<span className="text-primary-500">.TEAM</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-800 hover:text-foreground-900 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            <div className="relative">
              <button
                onClick={() => {
                  setIsDropdownOpen(!isDropdownOpen);
                  setIsGuideOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-foreground-800 hover:text-foreground-900 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>Categories</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)}></div>
                  <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-96 overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover rounded-lg" loading="lazy" />
                        </span>
                        <span className="font-medium">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => {
                  setIsGuideOpen(!isGuideOpen);
                  setIsDropdownOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-foreground-800 hover:text-foreground-900 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>Shopping Guide</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isGuideOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {isGuideOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsGuideOpen(false)}></div>
                  <div className="absolute top-full mt-2 left-0 w-80 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-96 overflow-y-auto z-50">
                    {allGuideArticles.map((article) => (
                      <Link
                        key={article.slug}
                        to={article.slug}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 cursor-pointer"
                        onClick={() => setIsGuideOpen(false)}
                      >
                        <span className="w-10 h-10 rounded-lg bg-background-100 flex-shrink-0 overflow-hidden">
                          <img src={article.image} alt={article.title} className="w-full h-full object-cover rounded-lg" loading="lazy" />
                        </span>
                        <div className="min-w-0">
                          <span className="block text-xs font-semibold text-primary-500 uppercase tracking-wider">{article.tag}</span>
                          <span className="block text-sm font-medium text-foreground-800 truncate">{article.title}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold cursor-pointer hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
            >
              VigorBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold cursor-pointer hover:bg-foreground-900 hover:text-background-50 transition-colors duration-200 whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-5 space-y-5 max-h-[80vh] overflow-y-auto">
          <div>
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-3 text-base font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors duration-150 cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-4-line text-lg"></i>
              </span>
              Home
            </Link>
          </div>

          <div>
            <label className="text-xs font-semibold text-foreground-400 uppercase tracking-[0.15em] mb-3 block">Categories</label>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150 whitespace-nowrap cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="w-8 h-8 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover rounded-md" loading="lazy" />
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-foreground-400 uppercase tracking-[0.15em] mb-3 block">Shopping Guides</label>
            <div className="space-y-1.5">
              {allGuideArticles.slice(0, 6).map((article) => (
                <Link
                  key={article.slug}
                  to={article.slug}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors duration-150 cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="w-8 h-8 rounded-md bg-background-100 flex-shrink-0 overflow-hidden">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover rounded-md" loading="lazy" />
                  </span>
                  <span className="text-xs font-medium leading-tight">{article.title}</span>
                </Link>
              ))}
              <div className="pt-1">
                <span className="block text-xs text-foreground-400 px-3">
                  + {allGuideArticles.length - 6} more guides available
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-2 border-t border-background-200">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full text-center px-5 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              VigorBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full text-center px-5 py-3 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}